import { useController, type Control, type FieldValues, type Path } from 'react-hook-form'
import { Label } from '@/components/ui/label'
import { MaskedInput } from './masked-input'
import { maskCep, stripDigits } from './masks'

export interface AddressData {
  street: string
  neighborhood: string
  city: string
  state: string
}

interface CepInputProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  disabled?: boolean
  onAddressFilled?: (data: AddressData) => void
}

export function CepInput<T extends FieldValues>({
  control,
  name,
  label = 'CEP',
  disabled,
  onAddressFilled,
}: CepInputProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })

  async function handleChange(raw: string) {
    field.onChange(raw)
    if (raw.length === 8 && onAddressFilled) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
        const data = await res.json() as Record<string, string>
        if (!data['erro']) {
          onAddressFilled({
            street: data['logradouro'] ?? '',
            neighborhood: data['bairro'] ?? '',
            city: data['localidade'] ?? '',
            state: data['uf'] ?? '',
          })
        }
      } catch {
        // silently fail — user fills address manually
      }
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={name}>{label}</Label>
      <MaskedInput
        id={name}
        value={field.value as string ?? ''}
        onChange={handleChange}
        mask={maskCep}
        strip={stripDigits}
        maxRaw={8}
        placeholder="00000-000"
        aria-invalid={!!error}
        disabled={disabled}
        onBlur={field.onBlur}
      />
      {error && <p className="text-xs text-destructive">{error.message}</p>}
    </div>
  )
}
